/* @jsx h */
import { defineComponent, ref, h } from "vue";
import { TopCategoriesDatum } from "@lemmonhq/types";
import { formatPriceInLocale } from "@lemmonhq/orders";
import { useLocalization, DateRange } from "@lemmonhq/uikit";
import { RouterLink } from "vue-router";

export type TopCategoriesDisplayDatum = TopCategoriesDatum & {
  restaurant_name?: string;
};

export const TopCategories = defineComponent<{
  data: TopCategoriesDisplayDatum[];
  totalAmount: number;
  dateRange: DateRange;
  currency: string;
  showDetailLink?: boolean;
}>({
  name: "TopCategories",
  props: ["data", "totalAmount", "dateRange", "currency", "showDetailLink"],
  setup(props) {
    const showRevenue = ref(true);
    const { t } = useLocalization();
    return () => {
      const totalCount = props.data.reduce((sum, d) => sum + d.total_count, 0);

      const queryParams = new URLSearchParams({
        type: "categories",
        startDate: props.dateRange.startDate,
        endDate: props.dateRange.endDate,
        dateRangeType: props.dateRange.type,
      }).toString();

      return (
        <div class="card transition-shadow border rounded-lg md:rounded-box">
          <div class="card-body p-4 md:p-6">
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-3">
                <h3 class="card-title flex items-center gap-2 text-accent font-normal">
                  <span class="material-symbols-outlined">folder</span>
                  {t("reports.dashboard.top_categories")}
                </h3>
                {props.showDetailLink !== false ? (
                  <RouterLink
                    to={`/app/reports/data?${queryParams}`}
                    class="hidden lg:flex text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    all →
                  </RouterLink>
                ) : null}
              </div>
              <div class="join">
                <button
                  class={`join-item btn btn-sm ${
                    showRevenue.value ? "btn-active" : ""
                  }`}
                  onClick={() => (showRevenue.value = true)}
                >
                  <span class="material-symbols-outlined sm:hidden text-base">
                    payments
                  </span>
                  <span class="hidden sm:inline">
                    {t("reports.dashboard.revenue")}
                  </span>
                </button>
                <button
                  class={`join-item btn btn-sm ${
                    !showRevenue.value ? "btn-active" : ""
                  }`}
                  onClick={() => (showRevenue.value = false)}
                >
                  <span class="material-symbols-outlined sm:hidden text-base">
                    tag
                  </span>
                  <span class="hidden sm:inline">
                    {t("reports.dashboard.count")}
                  </span>
                </button>
              </div>
            </div>
            <div class="space-y-3 md:space-y-4 pt-4 md:pt-6">
              {props.data
                .filter((el) => el.total_count > 0)
                .sort((a, b) => {
                  if (showRevenue.value) {
                    return b.total_revenue - a.total_revenue;
                  } else {
                    return b.total_count - a.total_count;
                  }
                })
                .slice(0, 5)
                .map((item, index) => {
                  const value = showRevenue.value
                    ? item.total_revenue
                    : item.total_count;
                  const total = showRevenue.value
                    ? props.totalAmount
                    : totalCount;
                  const percentage = (value / total) * 100;

                  return (
                    <div
                      key={`${item.restaurant_name ?? "restaurant"}:${item.category_id}`}
                      class="flex items-center gap-4 p-2 rounded-lg transition-colors"
                    >
                      <div class="w-8 h-8 rounded-full bg-secondary flex items-center justify-center font-normal text-secondary-content">
                        {index + 1}
                      </div>
                      <div class="flex-1 font-normal text-accent">
                        <div class="">{item.category_name}</div>
                        <div class="text-sm opacity-60">
                          {item.total_count} items sold
                          {item.restaurant_name
                            ? ` · ${item.restaurant_name}`
                            : ""}
                        </div>
                      </div>
                      <div class="text-right">
                        <div class="font-normal text-accent">
                          {showRevenue.value
                            ? formatPriceInLocale(
                                item.total_revenue,
                                true,
                                2,
                                props.currency,
                              )
                            : item.total_count.toString()}
                        </div>
                        {percentage > 0 && (
                          <div class="text-sm opacity-60">
                            {percentage.toFixed(1)}% of{" "}
                            {showRevenue.value ? "sales" : "orders"}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      );
    };
  },
});
